import type { AlertRuleSpec, AlertTrigger } from "@screener/protocol";
import { parseAmountField } from "@/lib/amount";
import {
  alertTriggerLabel,
  exchangeLabel,
  formatChipUsd,
  formatUsdExact,
  MIN_USD_QUICK_VALUES,
  marketLabel,
  sideLabel,
} from "@/lib/labels";
import type { RuleForm } from "./rule-form";

/**
 * Подписи §8 экрана «Алерты»: пояснения восьми триггеров (имена — из общей
 * карты `@/lib/labels`, stage6-acceptance-debts, решение 9), строка фильтра
 * правила, подсказка поля объёма. Группировка разрядов суммы и форматтер
 * точной суммы — тоже в `@/lib/labels` (`formatUsdExact`), рядом с
 * компактным `formatUsd`.
 */

/**
 * Возвращает типизированные ключи объекта — `Object.keys` в TypeScript
 * всегда даёт `string[]`, даже когда объект набран через
 * `satisfies Record<K, V>`. Приём confined к одной обёртке (ревью
 * stage6-alert-web, пункт C.5): единственный `as` в этом файле, не про
 * недоверенное внешнее значение, а про то, что сам язык не типизирует
 * `Object.keys` точнее.
 */
function recordKeys<K extends string>(record: Record<K, unknown>): readonly K[] {
  return Object.keys(record) as K[];
}

/**
 * Пояснения по триггеру — полнота проверяется компилятором:
 * `satisfies Record<AlertTrigger, string>` не соберётся, если протокол
 * заводит новый вариант, а эта карта его не описывает. Подпись самого
 * триггера — общая (`alertTriggerLabel`), здесь только hint. Пояснения
 * tested/held/moved и завала — по ревью (пункт E.8), точнее описывают
 * переход FSM.
 */
const TRIGGER_HINTS = {
  confirmed: "стена подтвердилась",
  approached: "цена подошла к стене",
  tested: "стену начали есть сделками",
  held: "цена отскочила от стены",
  eaten: "стену съели сделками",
  pulled: "стену сняли",
  moved: "крупнейший уровень сместился внутри стены",
  situation_stacked: "две и больше стен одной стороны рядом на одном рынке",
} satisfies Record<AlertTrigger, string>;

// Порядок §8 сохранён: ключи объекта литерала — это и есть порядок вставки,
// а `Object.keys` строковых (не числоподобных) ключей отдаёт их в этом же
// порядке (ECMA-262 `OrdinaryOwnPropertyKeys`).
export const TRIGGERS: readonly { value: AlertTrigger; label: string; hint: string }[] = recordKeys(
  TRIGGER_HINTS,
).map((value) => ({ value, label: alertTriggerLabel(value), hint: TRIGGER_HINTS[value] }));

export function volumeFieldHint(trigger: AlertTrigger): string {
  return trigger === "situation_stacked"
    ? "Сравнивается сумма наибольших размеров стен завала"
    : "Сравнивается наибольший размер стены за её жизнь";
}

/** Подпись флажка «без ММ» формы — завал говорит про завалы, а не про стены (ревью, пункт E.9). */
export function mmFilterFieldLabel(trigger: AlertTrigger): string {
  return trigger === "situation_stacked"
    ? "Без завалов, где все стены с признаками маркетмейкера"
    : "Без стен с признаками маркетмейкера";
}

/**
 * Границы объёма словами: «от $X», «$X – $Y», «до $Y»; у завала впереди
 * «суммарно». Сужение через `if` вместо приведения типа (ревью
 * stage6-alert-web, пункт C.6): ветки держатся на собственных проверках
 * `!== null`, а не на приведении `as string`.
 */
function volumeText(
  min: string | null,
  max: string | null,
  trigger: AlertTrigger,
  withPrefix: boolean,
): string | null {
  const prefix = withPrefix && trigger === "situation_stacked" ? "суммарно " : "";
  if (min !== null && max !== null) {
    return `${prefix}${formatUsdExact(min)} – ${formatUsdExact(max)}`;
  }
  if (min !== null) {
    return `${prefix}от ${formatUsdExact(min)}`;
  }
  if (max !== null) {
    return `${prefix}до ${formatUsdExact(max)}`;
  }
  return null;
}

/** Размер правила для колонки таблицы и строки фильтра (1.103, §116 п. 1); границ нет — `null`. */
export function volumePart(spec: AlertRuleSpec): string | null {
  return volumeText(spec.min_usd, spec.max_usd, spec.trigger, true);
}

/** Границы объёма без слова «суммарно» — видимый текст колонки таблицы, где места мало. */
export function volumeRange(spec: AlertRuleSpec): string | null {
  return volumeText(spec.min_usd, spec.max_usd, spec.trigger, false);
}

/** Строка фильтра правила через « · », в порядке §8. Нет полей — «любая плотность». */
export function ruleFilterLabel(spec: AlertRuleSpec): string {
  const parts: string[] = [];
  if (spec.base !== null) {
    parts.push(spec.base);
  }
  if (spec.exchange !== null) {
    parts.push(exchangeLabel(spec.exchange));
  }
  if (spec.market !== null) {
    parts.push(marketLabel(spec.market));
  }
  if (spec.side !== null) {
    parts.push(sideLabel(spec.side));
  }
  const volume = volumePart(spec);
  if (volume !== null) {
    parts.push(volume);
  }
  if (spec.favorite_only) {
    parts.push("только избранные");
  }
  if (spec.exclude_mm) {
    // Завал — это несколько стен: «без ММ» одной стены было бы неточно
    // (ревью, пункт E.9).
    parts.push(spec.trigger === "situation_stacked" ? "без ММ-завалов" : "без ММ");
  }
  return parts.length === 0 ? "любая плотность" : parts.join(" · ");
}

/**
 * Быстрые значения объёма «от» формы правила (1.103, §116 п. 5): те же числа и
 * подписи, что в панели фильтров экрана плотностей (`MIN_USD_QUICK_VALUES`),
 * но десятичными строками — поле «от» формы держит строку.
 */
export const QUICK_VOLUMES: readonly { value: string; label: string }[] = MIN_USD_QUICK_VALUES.map(
  (amount) => ({ value: String(amount), label: formatChipUsd(amount) }),
);

/** Читает поле суммы; нечитаемое и пустое — `null`. */
function readAmount(raw: string): string | null {
  const parsed = parseAmountField(raw);
  return parsed.ok ? parsed.value : null;
}

/**
 * Быстрое значение, равное введённому в «от», иначе `null`. Сравнение по
 * числу (§66): «1м» и «1 000 000» — одно и то же 1M.
 */
export function quickVolumeSelected(draft: Pick<RuleForm, "minUsd">): string | null {
  const min = readAmount(draft.minUsd);
  return QUICK_VOLUMES.find((quick) => quick.value === min)?.value ?? null;
}

/** Нажатие на быстрое значение: невыбранное пишет в «от», выбранное очищает поле. */
export function toggleQuickVolume(minUsd: string, value: string): string {
  return quickVolumeSelected({ minUsd }) === value ? "" : value;
}

/** Действия в итоге формы: слова строчные, имя Telegram — как есть. */
const SUMMARY_ACTIONS = [
  { key: "sound", text: "звук" },
  { key: "notify", text: "уведомления" },
  { key: "telegram", text: "Telegram" },
  { key: "log", text: "лог" },
] as const satisfies readonly { key: "sound" | "notify" | "telegram" | "log"; text: string }[];

/**
 * Итог формы правила одной фразой (1.103, §116 п. 5): «Когда Confirmed — LTC ·
 * PERP · от $2 000 000 → звук, Telegram.». Незаданные рынок, сторона, объём и
 * пометки во фразу не идут; без действий фраза просит выбрать хотя бы одно.
 * Нечитаемая граница объёма пропускается — ошибку поля показывает сама форма.
 */
export function ruleSummary(draft: RuleForm): string {
  const actions = SUMMARY_ACTIONS.filter((action) => draft[action.key]).map(
    (action) => action.text,
  );
  if (actions.length === 0) {
    return "Выберите хотя бы одно действие.";
  }
  const base = draft.base.trim().toUpperCase();
  const parts: string[] = [base === "" ? "любая монета" : base];
  if (draft.market !== null) {
    parts.push(marketLabel(draft.market));
  }
  if (draft.side !== null) {
    parts.push(sideLabel(draft.side));
  }
  const volume = volumeText(
    readAmount(draft.minUsd),
    readAmount(draft.maxUsd),
    draft.trigger,
    true,
  );
  if (volume !== null) {
    parts.push(volume);
  }
  if (draft.favoriteOnly) {
    parts.push("только избранные");
  }
  if (draft.excludeMm) {
    parts.push(draft.trigger === "situation_stacked" ? "без ММ-завалов" : "без ММ");
  }
  return `Когда ${alertTriggerLabel(draft.trigger)} — ${parts.join(" · ")} → ${actions.join(", ")}.`;
}
